import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { functionErrorMessage } from "@/lib/functionError";


interface EmailVerificationCodeInputProps {
  email: string;
  userId?: string | null;
  onVerified: () => void | Promise<void>;
  onBack: () => void;
  backLabel?: string;
}

export const EmailVerificationCodeInput = ({
  email,
  userId,
  onVerified,
  onBack,
  backLabel = "Nieuw account",
}: EmailVerificationCodeInputProps) => {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1800); // 30 minutes in seconds (matches code TTL)
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { toast } = useToast();

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-verify if all fields are filled
    if (newCode.every((digit) => digit !== "")) {
      handleVerify(newCode.join(""));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    
    // Check if pasted data is 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split("");
      setCode(newCode);
      inputRefs.current[5]?.focus();
      
      // Auto-verify
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (codeToVerify?: string) => {
    const verificationCode = codeToVerify || code.join("");
    
    if (verificationCode.length !== 6) {
      toast({
        variant: "destructive",
        title: "Ongeldige code",
        description: "Voer alle 6 cijfers in",
      });
      return;
    }

    setIsVerifying(true);

    try {
      const { data, error } = await supabase.functions.invoke(
        userId ? "verify-email-code" : "verify-email-code-by-email",
        {
          body: userId
            ? { userId, code: verificationCode }
            : { email, code: verificationCode },
        }
      );

      if (error) {
        throw new Error(
          await functionErrorMessage(error, "De code is ongeldig of verlopen"),
        );
      }

      if (data?.valid) {
        toast({
          title: "Email geverifieerd! 🎉",
          description: "Je kunt nu aan de slag met ChillWerk",
        });
        await onVerified();
      } else {
        throw new Error(data?.error || "Ongeldige code");
      }
    } catch (error: any) {
      console.error("Verification error:", error);
      toast({
        variant: "destructive",
        title: "Verificatie mislukt",
        description: error.message || "De code is ongeldig of verlopen",
      });
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setIsResending(true);

    try {
      const { error } = await supabase.functions.invoke(
        userId ? "resend-verification-code" : "request-email-verification-code",
        { body: userId ? { userId, email } : { email } }
      );

      if (error) {
        throw new Error(
          await functionErrorMessage(error, "Probeer het later opnieuw"),
        );
      }

      toast({
        title: "Code opnieuw verzonden",
        description: "Check je inbox (en spam folder!)",
      });

      setResendCooldown(60); // 60 seconds cooldown
      setTimeLeft(1800); // Reset timer (code is 30 min valid)
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } catch (error: any) {
      console.error("Resend error:", error);
      toast({
        variant: "destructive",
        title: "Fout bij opnieuw verzenden",
        description: error.message || "Probeer het later opnieuw",
      });
    } finally {
      setIsResending(false);
    }
  };


  return (
    <div className="w-full max-w-sm mx-auto space-y-2 px-4 -mt-4">
      {/* === SECTION 1: Verificatie === */}
      <div className="space-y-2">
        {/* Compact header with emoji */}
        <div className="text-center space-y-1">
          <span className="inline-block bg-secondary/20 text-secondary-foreground px-3 py-0.5 rounded-full text-xs font-bold">
            ✨ Je bent er bijna!
          </span>
          <h2 className="text-xl sm:text-2xl font-black text-foreground flex items-center justify-center gap-2">
            📧 Verifieer je Email
          </h2>
        </div>

        {/* Email display - prominent */}
        <div className="bg-muted/50 rounded-lg p-2 text-center">
          <p className="text-xs text-muted-foreground">Code verstuurd naar:</p>
          <p className="font-bold text-foreground text-sm break-all">{email}</p>
        </div>

        {/* Code input */}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground block text-center">
            Voer je 6-cijferige code in:
          </label>
          <div className="flex gap-1.5 justify-center" onPaste={handlePaste}>
            {code.map((digit, index) => (
              <Input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-10 h-11 text-center text-lg font-bold p-0"
                disabled={isVerifying}
                autoFocus={index === 0}
              />
            ))}
          </div>
        </div>

        {/* Timer */}
        {timeLeft > 0 && (
          <p className="text-center text-xs text-muted-foreground">
            ⏱️ Geldig nog: <span className="font-semibold text-foreground">{formatTime(timeLeft)}</span>
          </p>
        )}

        {timeLeft === 0 && (
          <p className="text-center text-xs text-destructive font-semibold">
            ⚠️ Code verlopen - vraag een nieuwe aan
          </p>
        )}

        {/* Yellow verify button */}
        <Button
          onClick={() => handleVerify()}
          disabled={code.some((digit) => digit === "") || isVerifying || timeLeft === 0}
          className="w-full h-11 bg-secondary hover:bg-secondary/90 text-secondary-foreground font-bold text-base"
        >
          {isVerifying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Verifieer 🚀
        </Button>

        {/* Spam tip - important warning */}
        <p className="text-center text-xs font-medium text-destructive bg-destructive/10 border border-destructive/20 rounded-lg py-1.5 px-3">
          ⚠️ Check ook je spam/ongewenste mail!
        </p>
      </div>

      {/* === SECTION 2: Hulp nodig? === */}
      <div className="bg-muted/30 rounded-xl p-3 space-y-2 border border-border/50">
        <p className="text-center text-xs font-semibold text-muted-foreground">
          Lukt het niet?
        </p>
        
        {/* Resend button */}
        <div className="text-center">
          <Button
            variant="outline"
            size="sm"
            onClick={handleResend}
            disabled={resendCooldown > 0 || isResending}
            className="text-xs h-8 px-4"
          >
            {isResending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
            {resendCooldown > 0 ? `Wacht ${resendCooldown}s` : "Code opnieuw versturen"}
          </Button>
        </div>

        {/* Wrong email hint */}
        <p className="text-center text-xs text-muted-foreground">
          Verkeerde email? <button onClick={onBack} className="text-primary underline hover:no-underline font-medium">{backLabel}</button> 💪
        </p>
      </div>

      {/* === SECTION 3: Inspiratie === */}
      <div className="pt-2 border-t border-border/50">
        <p className="text-center text-xs text-muted-foreground/80 italic">
          Iedere dag vinden mensen via ChillWerk een nieuw avontuur ✨
        </p>
      </div>
    </div>
  );
};
